import { cn } from '@foundation/utils/cn';

import {
  HeadlessCheckbox,
  type HeadlessCheckboxProps,
  type HeadlessCheckboxRootProps,
} from '@design-system/headless-ui';
import { Check, Minus } from '@design-system/icons';

export type CheckboxProps = Omit<
  HeadlessCheckboxProps,
  | 'className'
  | 'indicatorClassName'
  | 'wrapperClassName'
  | 'labelClassName'
  | 'descriptionClassName'
  | 'errorClassName'
  | 'icon'
> & {
  /** When false, the label stays in the DOM (still the accessible name via
   * `for`/`id`) but is visually hidden - never swap it for a separate
   * `aria-label`, which risks drifting from the translated visible text. */
  readonly showLabel?: boolean;
  /** Fired when the checked state changes */
  readonly onCheckedChange?: HeadlessCheckboxRootProps['onCheckedChange'];
};

// Dash on the indicator, not the path - Base UI times the unmount from
// getAnimations() there. 27 = the icon path length (26.6) rounded up - a
// shorter value clips the tip of the checkmark since the dash pattern repeats.
const DRAW_TICK = cn(
  '[stroke-dasharray:27] [stroke-dashoffset:0]',
  'transition-[stroke-dashoffset] duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
  'data-starting-style:[stroke-dashoffset:27]',
  'data-ending-style:[stroke-dashoffset:27] data-ending-style:duration-[150ms]',
  'motion-reduce:transition-none',
);

export function Checkbox({
  error,
  description,
  indeterminate,
  showLabel = true,
  ...props
}: CheckboxProps) {
  return (
    <HeadlessCheckbox
      {...props}
      indeterminate={indeterminate}
      description={description}
      error={error}
      icon={indeterminate ? <Minus size={16} /> : <Check size={16} />}
      className={cn(
        'size-5 transition-colors',
        'data-unchecked:bg-pmui-bg-primary data-unchecked:border-pmui-border-primary data-unchecked:shadow-sm',
        'data-unchecked:hover:border-pmui-fg-primary',
        'data-checked:bg-pmui-bg-brand-root data-checked:border-pmui-bg-brand-root',
        'data-indeterminate:bg-pmui-bg-brand-root data-indeterminate:border-pmui-bg-brand-root',
        'data-checked:hover:bg-pmui-interactive-bg-brand-hovered data-checked:hover:border-pmui-interactive-bg-brand-hovered',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-pmui-border-focus-ring-focus',
        'data-disabled:opacity-40',
        error &&
          'data-unchecked:border-pmui-border-status-negative data-unchecked:bg-pmui-bg-status-negative-subtle',
      )}
      indicatorClassName={cn(
        'text-pmui-fg-on-brand',
        !indeterminate && DRAW_TICK,
      )}
      wrapperClassName="text-pmui-fg-primary has-data-disabled:text-pmui-interactive-fg-disabled"
      labelClassName={cn(!showLabel && 'sr-only')}
      descriptionClassName="text-sm leading-5 tracking-tightest text-pmui-fg-secondary"
      errorClassName="text-sm leading-5 tracking-tightest text-pmui-fg-status-negative-default"
    />
  );
}
